import { message, Modal } from "antd";
import Card from "antd/es/card/Card";
import Sources from "entities/Sources";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAdd } from "react-icons/md";
import Header from "shared/components/Header";
import Search from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CREATE_SOURCE,
  DELETE_SOURCE,
  START_SOURCE,
  STOP_SOURCE,
  UPDATE_SOURCE,
} from "utils/api/graphql/mutations/sources";
import FormDrawer from "../FormDrawer";
import { toSourcePayload } from "../form";
import View from "./View";

const baseUrl = process.env.REACT_APP_BASE_URL;

const List = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SOURCE);
  const LIST = t("LIST", { returnObjects: true });
  const CONFIRM = t("CONFIRM", { returnObjects: true });
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState("");
  const [editedSource, setEditedSource] = useState(null);
  const filter = {
    like: isEmpty(search)
      ? undefined
      : { OR: [{ name: `%${search}%` }, { platform: `%${search}%` }] },
  };

  const mutationOptions = {
    refetchQueries: ["AdminSources"],
    awaitRefetchQueries: true,
  };
  const closeDrawer = () => setEditedSource(null);
  const [addSource, { loading: adding }] = useMutation(CREATE_SOURCE, {
    ...mutationOptions,
    onCompleted: closeDrawer,
  });
  const [updateSource, { loading: updating }] = useMutation(UPDATE_SOURCE, {
    ...mutationOptions,
    onCompleted: closeDrawer,
  });
  const [deleteSource] = useMutation(DELETE_SOURCE, mutationOptions);
  const [stopSource] = useMutation(STOP_SOURCE, mutationOptions);
  const [startSource] = useMutation(START_SOURCE, mutationOptions);

  const confirm = (content, onOk) =>
    Modal.confirm({
      content,
      okText: CONFIRM.OK,
      cancelText: CONFIRM.CANCEL,
      onOk,
    });

  const copy = (link) =>
    navigator.clipboard
      .writeText(link)
      .then(() => message.success(LIST.LINK_COPIED))
      .catch(() => message.error(LIST.LINK_COPY_ERROR));

  const submit = (values, { initial }) => {
    const sourceId = get(editedSource, "id");
    const data = toSourcePayload(values, { initial });
    if (!sourceId) {
      return addSource({ variables: { data: { ...data, baseUrl } } });
    }
    if (isEmpty(data)) return closeDrawer();
    return updateSource({
      variables: { where: { id: sourceId }, data: { ...data, baseUrl } },
    });
  };

  const actions = {
    edit: (source) => setEditedSource(source),
    copyGuestLink: ({ guestLink }) => copy(guestLink),
    copyAuthenticatedLink: ({ authenticatedLink }) => copy(authenticatedLink),
    stop: ({ id }) =>
      confirm(CONFIRM.STOP, () => stopSource({ variables: { where: { id } } })),
    start: ({ id }) =>
      confirm(CONFIRM.START, () =>
        startSource({ variables: { where: { id } } }),
      ),
    remove: ({ id }) =>
      confirm(CONFIRM.DELETE, () =>
        deleteSource({ variables: { where: { id } } }),
      ),
  };

  return (
    <>
      <Header
        title={LIST.HEADER.TITLE}
        actions={[
          {
            icon: <MdAdd size={24} />,
            text: LIST.HEADER.SAVE,
            onClick: () => setEditedSource({}),
          },
        ]}
      />
      <Card type="simple">
        <div className="m-bottom-20 grid--4">
          <Search
            placeholder={LIST.SEARCH}
            type="search"
            onChange={({ target: { value } }) => {
              setPagination((prev) => ({ ...prev, skip: 0 }));
              setSearch(value);
            }}
          />
        </div>
        <Sources
          View={View}
          filter={{ ...filter, ...pagination }}
          setValues={setPagination}
          skip={pagination.skip || 0}
          NoData={View}
          actions={actions}
        />
      </Card>
      {editedSource && (
        <FormDrawer
          source={editedSource}
          onSubmit={submit}
          onClose={closeDrawer}
          submitting={adding || updating}
        />
      )}
    </>
  );
};

export default List;
